// Stakeholder map display labels + a small relative-time helper.
// FE-SCRM-013.

import type {
  DecisionRole,
  InfluenceLevel,
  Stance,
  StakeholderRiskCode,
} from "./types";


export const DECISION_ROLE_LABEL: Record<DecisionRole, string> = {
  economic_buyer: "Economic buyer",
  champion: "Champion",
  coach: "Coach",
  technical_evaluator: "Technical evaluator",
  user: "User",
  blocker: "Blocker",
  unknown: "Unknown",
};


export const INFLUENCE_LEVEL_LABEL: Record<InfluenceLevel, string> = {
  high: "High influence",
  medium: "Medium influence",
  low: "Low influence",
};


export const STANCE_LABEL: Record<Stance, string> = {
  supportive: "Supportive",
  neutral: "Neutral",
  skeptical: "Skeptical",
  opposed: "Opposed",
  unknown: "Unknown",
};


/** Producer-facing risk-code descriptions. The text mirrors the spec's
 * coaching-banner copy so the dashboard tile and the in-map banner
 * speak the same language. */
export const RISK_LABEL: Record<StakeholderRiskCode, string> = {
  missing_economic_buyer:
    "No economic buyer identified. This is a pursuit risk.",
  missing_champion:
    "No champion identified yet. Identify a champion to advance.",
  champion_is_user_role_only:
    "Champion lacks influence to advance the pursuit.",
  single_threaded:
    "Only one stakeholder mapped past Qualified — single-threaded.",
  opposed_stakeholder_unhandled:
    "An opposed stakeholder hasn't been worked.",
  cold_economic_buyer:
    "The economic buyer hasn't been touched recently.",
};


/** Quadrant identity for the influence × support layout. */
export type StakeholderQuadrant =
  | "supportive_high"
  | "supportive_low"
  | "skeptical_high"
  | "skeptical_low";


/** Map a (stance, influence) pair to one of the four quadrants. Neutral
 * and unknown stances fall to ``supportive`` side because they're not
 * actively blocking; producers want to see who's leaning in vs.
 * leaning out. Influence ``medium`` rolls up to ``high`` so two-level
 * quadrants don't fragment around the modal default. */
export function quadrantFor(
  stance: Stance, influence: InfluenceLevel,
): StakeholderQuadrant {
  const leaningOut = stance === "skeptical" || stance === "opposed";
  const highSide = influence === "high" || influence === "medium";
  if (leaningOut) {
    return highSide ? "skeptical_high" : "skeptical_low";
  }
  return highSide ? "supportive_high" : "supportive_low";
}


export const QUADRANT_LABEL: Record<StakeholderQuadrant, string> = {
  supportive_high: "Supportive · High influence",
  supportive_low: "Supportive · Low influence",
  skeptical_high: "Skeptical · High influence",
  skeptical_low: "Skeptical · Low influence",
};


export function formatRelativeAgo(
  iso: string | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const diff = Math.max(0, now - ts);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  if (diff < hour) {
    const n = Math.max(1, Math.floor(diff / minute));
    return `${n} minute${n === 1 ? "" : "s"} ago`;
  }
  if (diff < day) {
    const n = Math.floor(diff / hour);
    return `${n} hour${n === 1 ? "" : "s"} ago`;
  }
  if (diff < week) {
    const n = Math.floor(diff / day);
    return `${n} day${n === 1 ? "" : "s"} ago`;
  }
  if (diff < month) {
    const n = Math.floor(diff / week);
    return `${n} week${n === 1 ? "" : "s"} ago`;
  }
  const n = Math.floor(diff / month);
  return `${n} month${n === 1 ? "" : "s"} ago`;
}
